import { useRef, useState } from 'react';
import useDismiss from './useDismiss';
import { ladderWith, servingsWord } from './servings';

/** How many this dinner is planned for, and a way to change it.
 *
 * A week is chosen at one serving size, but the dinners on it are not all the
 * same dinner: one added on Thursday may be feeding six when the other four
 * were for two. The shopping list buys per dinner, so this is the control
 * that makes it buy the right amount.
 *
 * Deliberately no confirmation, unlike its neighbour. Marking a dinner cooked
 * empties the pantry and cannot be undone, so it asks; re-planning one is
 * reversible by picking again, and a dialog in front of every size change
 * would be noise in front of the cheap half of the pair.
 */
export default function ServingsButton({
  recipeTitle,
  plannedServings,
  weekServings,
  shopped,
  pending,
  onChange,
}: {
  recipeTitle: string;
  /** What this dinner is planned for now. */
  plannedServings: number;
  /** The week's own figure, so the menu can say which option is the ordinary one. */
  weekServings: number;
  /** The shop is done, so a change here changes a list already bought from. */
  shopped: boolean;
  pending: boolean;
  onChange: (servings: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const group = useRef<HTMLDivElement>(null);

  useDismiss(open, group, () => setOpen(false));

  // The current size is dropped: the button already is that number, and
  // offering it again invites the reading that the two do different things.
  const others = ladderWith(plannedServings).filter(
    (count) => count !== plannedServings,
  );

  return (
    <div className="picker" ref={group}>
      <button
        className="btn btn--small"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Change how many ${recipeTitle} is planned for`}
        disabled={pending}
        onClick={() => setOpen((current) => !current)}
      >
        Serves {plannedServings} <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="picker__menu">
          <ul role="menu">
            {others.map((count) => (
              <li key={count} role="none">
                <button
                  role="menuitem"
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onChange(count);
                  }}
                >
                  Plan for {servingsWord(count)}
                  {/* Which one is the week's, so returning to it is a choice
                      rather than a coincidence of remembering the number. */}
                  {count === weekServings && (
                    <span className="muted small"> · as the week planned</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          {/* The only place this consequence is visible before it happens.
              The food for this week is already bought, so a bigger dinner
              puts its difference back onto the shopping list. */}
          {shopped && (
            <p className="picker__note muted small">
              You have already shopped, so this adds to your list. Put the rest
              away from the shopping list.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
